import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Typography, Link, List, ListItem, Paper, Grid, ListItemText, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../styles';
import LinkIcon from '@mui/icons-material/Link';

const StyledSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  backgroundColor: colors.white,
  marginBottom: theme.spacing(3),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  position: 'relative'
}));

const FloatingMenu = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(4),
  backgroundColor: colors.white,
  width: '100%',
  marginRight: theme.spacing(4),
  alignSelf: 'flex-start',
  boxShadow: 'none',
  borderRadius: '20px',
  padding: theme.spacing(2)
}));

const MenuLink = styled(ListItem)(({ theme, active }) => ({
  borderRadius: '0.5rem',
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: 'transform 80ms cubic-bezier(0, 0.3, 1, 0.5), background 80ms cubic-bezier(0, 0.3, 1, 0.5)',
  '&:hover': {
    backgroundColor: active ? `${theme.palette.primary.main}20` : 'rgba(0, 0, 0, 0.04)',
    borderRadius: '0.5rem'
  },
  '&:active': {
    transform: 'scale(0.95)'
  },
  ...(active && {
    backgroundColor: `${theme.palette.primary.main}20`
  }),
  '& .MuiTypography-root': {
    fontWeight: active ? 900 : 600,
    fontSize: '1.1rem'
  }
}));

const Section = ({ id, title, children, onVisible }) => {
  const sectionRef = useRef(null);
  const [shouldObserve, setShouldObserve] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShouldObserve(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldObserve) return;
    const node = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          onVisible && onVisible(id);
        }
      },
      { threshold: 0.5, rootMargin: '-80px 0px' }
    );
    if (node) observer.observe(node);
    return () => { if (node) observer.unobserve(node); };
  }, [id, onVisible, shouldObserve]);

  return (
    <StyledSection ref={sectionRef}>
      <div id={id} style={{ position: 'absolute', top: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.primary }}>
          {title}
        </Typography>
        <Tooltip title="Copy link" placement="left">
          <IconButton
            size="small"
            aria-label={`Copy link to ${title}`}
            onClick={() => {
              try {
                const origin = window.location.origin;
                const path = window.location.pathname;
                const url = `${origin}${path}#${id}`;
                navigator.clipboard.writeText(url);
              } catch (error) {
                console.error('Failed to copy section link', error);
              }
            }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ color: colors.secondary }}>{children}</Box>
    </StyledSection>
  );
};

const Terms = () => {
  const [activeSection, setActiveSection] = useState('acceptance-of-terms');
  const [isManualScroll, setIsManualScroll] = useState(false);
  const scrollTimeout = useRef(null);

  const sections = useMemo(() => [
    { id: 'acceptance-of-terms', label: 'Acceptance of Terms' },
    { id: 'use-of-the-service', label: 'Use of the Service' },
    { id: 'ownership-of-the-service', label: 'Ownership of the Service' },
    { id: 'financial-connections', label: 'Financial Connections' },
    { id: 'subscriptions-and-billing', label: 'Subscriptions & Billing' },
    { id: 'rewards-program', label: 'Rewards & Contests' },
    { id: 'user-generated-content', label: 'User-Generated Content' },
    { id: 'financial-data-and-ai', label: 'Financial Data & AI' },
    { id: 'copyright-complaints-dmca', label: 'Copyright (DMCA)' },
    { id: 'ai-and-insights', label: 'AI & Insights' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'disclaimers', label: 'Disclaimers' },
    { id: 'limitation-of-liability', label: 'Limitation of Liability' },
    { id: 'termination', label: 'Termination' },
    { id: 'governing-law', label: 'Governing Law & Disputes' },
    { id: 'changes-to-terms', label: 'Changes to Terms' },
    { id: 'contact', label: 'Contact' }
  ], []);

  const handleSectionVisible = useCallback((id) => {
    if (isManualScroll) return;
    setActiveSection(id);
    try {
      const path = window.location.pathname;
      window.history.replaceState(null, '', `${path}#${id}`);
    } catch (error) {
      console.warn('Unable to update URL hash', error);
    }
  }, [isManualScroll]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    setIsManualScroll(true);
    setActiveSection(sectionId);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.scrollBy({ top: -80, behavior: 'smooth' });
    scrollTimeout.current = setTimeout(() => setIsManualScroll(false), 1000);
    try {
      const path = window.location.pathname;
      window.history.replaceState(null, '', `${path}#${sectionId}`);
    } catch (error) {
      console.warn('Unable to update URL hash', error);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sections.some(s => s.id === hash)) {
      const timer = setTimeout(() => scrollToSection(hash), 300);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const SideMenu = () => (
    <FloatingMenu>
      <List>
        {sections.map((s) => (
          <MenuLink key={s.id} active={activeSection === s.id} onClick={() => scrollToSection(s.id)} disableRipple>
            <ListItemText 
              primary={s.label}
              primaryTypographyProps={{
                fontWeight: activeSection === s.id ? 900 : 600,
                fontSize: '0.95rem',
                color: 'inherit'
              }}
            />
          </MenuLink>
        ))}
      </List>
    </FloatingMenu>
  );

  return (
    <Box sx={{
      backgroundColor: colors.background,
      minHeight: '100vh',
      py: { xs: 6, md: 10 },
      overflowY: 'auto',
      height: '100vh',
      position: 'fixed',
      width: '100%'
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, color: colors.primary, mb: 1 }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.8 }}>
            Last updated: 8/7/2026
          </Typography>
        </Box>

        <Grid container spacing={0}>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
            <SideMenu />
          </Grid>

          <Grid item xs={12} md={9} sx={{ pr: { xs: 0, md: 4 } }}>

        <Section id="acceptance-of-terms" title="Acceptance of Terms" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            These Terms of Service ("Terms") are a legal agreement between you and Bleau Consulting LLC, a Kansas limited liability company doing business as Budgit ("Budgit," "we," "our," or "us"), governing your access to and use of the Budgit website, web application, and mobile applications (collectively, the "Service").
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Business contact:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, whiteSpace: 'pre-line' }}>
            {`Bleau Consulting LLC, doing business as Budgit
11985 S. Monticello Terrace
Olathe, KS 66061
support@budgit.app`}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
          </Typography>
        </Section>

        <Section id="use-of-the-service" title="Use of the Service" onVisible={handleSectionVisible}>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You must be at least 18 years old (or the age of majority in your jurisdiction, whichever is greater) to use the Service. The Service is not offered to persons under 18. By creating an account, you represent that you meet this requirement.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You are responsible for maintaining the confidentiality of your account and PIN.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You agree not to misrepresent your identity or use the Service for unlawful purposes.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">We reserve the right to suspend or terminate accounts that violate these Terms, misuse the Service, or engage in unlawful activity.</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="ownership-of-the-service" title="Ownership of the Service" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            The Service, including its software, design, text, graphics, logos, trademarks (including BUDGIT and related marks), and all other content we provide (excluding User Content and Financial Data as defined below), is owned by Bleau Consulting LLC or its licensors and is protected by intellectual property laws. Except for the limited right to use the Service under these Terms, no rights are granted to you. You may not copy, modify, distribute, sell, or reverse engineer any part of the Service except as expressly permitted by law.
          </Typography>
        </Section>

        <Section id="financial-connections" title="Financial Connections via Plaid" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Budgit uses Plaid to enable secure connections to your financial accounts. By linking an account, you authorize Budgit to share your information with Plaid and authorize Plaid to obtain data from your financial institution, subject to Plaid's terms and privacy policy.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            See also our <Link href="/privacy">Privacy Policy</Link> and Plaid's
            {' '}<Link href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer">End User Privacy Policy</Link>.
          </Typography>
        </Section>

        <Section id="subscriptions-and-billing" title="Subscriptions and Billing" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Premium features may require a paid subscription. Subscriptions may be purchased and managed through Apple In‑App Purchase on iOS devices, or through our payment processor (currently Stripe) on web and Android. By starting a subscription, you authorize recurring charges until cancellation. Subscription price, duration, and any free trial terms are shown at the time of purchase.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>iOS Terms of Use:</strong> Apple’s Standard End User License Agreement applies to Budgit on iOS. See
            {' '}<Link href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer">Apple’s Standard EULA</Link>.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Auto-renewable subscriptions renew automatically unless canceled.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>iOS (Apple)</strong>: Payment will be charged to your Apple ID account at confirmation of purchase. Your subscription will automatically renew unless auto-renew is turned off at least 24 hours before the end of the current period. Your account will be charged for renewal within 24 hours prior to the end of the current period. You can manage or cancel your subscription in your device Settings &gt; Apple ID &gt; Subscriptions. Deleting the App does not cancel your subscription.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Web/Android (Stripe)</strong>: Subscriptions can be managed or canceled through in-app settings or the billing portal when available. Cancellation typically takes effect at the end of the current billing cycle.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">If a free trial is offered, it will automatically convert to a paid subscription unless canceled before the trial ends.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">We do not provide refunds for partially used billing periods, except where required by law.</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="rewards-program" title="Rewards & Contests" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Monthly Budgeting Challenge
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Budgit offers a Monthly Budgeting Challenge, a skill-based competition where participants compete on a public leaderboard. Winners are determined by their budgeting performance and may receive cash prizes. <strong>This is a skill-based contest</strong>; no element of chance is involved in determining winners.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <Link href="/contest-rules">View the Official Contest Rules</Link> for complete eligibility requirements, scoring methodology, prize details, and legal terms.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600, mt: 3 }}>
            General Rewards
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Budgit may also offer additional rewards programs or promotional incentives from time to time.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Eligibility: Rewards may be limited by age, jurisdiction, or compliance with program rules. You must have an active account in good standing to participate.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">No Guarantee: Rewards (outside of the Monthly Challenge) are discretionary, not guaranteed, and may be modified or discontinued at any time without notice.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Taxes: Users are responsible for reporting and paying any applicable taxes on all rewards and prizes.</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="user-generated-content" title="User-Generated Content" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            The Service lets you submit and display content ("User Content"), including, without limitation, profile images and names, achievement events, streaks, scores, contest entries, comments, reactions, and posts to leaderboards or community feeds. You retain all ownership rights in your User Content.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Financial Data is not User Content. The license in this section does not apply to Financial Data; the "Financial Data and AI Processing" section governs that data instead.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            License You Grant to Budgit
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            By submitting User Content to the Service, you grant Budgit a non-exclusive, worldwide, royalty-free, sublicensable, and transferable license to host, store, cache, reproduce, publish, publicly display, publicly perform, communicate, distribute, adapt, translate, and otherwise use your User Content solely for the purpose of operating, providing, promoting, and improving the Service, including within features such as leaderboards, Discover, community feeds, achievement events, and contest displays. This license continues for as long as your User Content remains on the Service, including for a reasonable period after you remove it or delete your account so that we can complete backups, audits, log retention, and the fulfillment of any active contest awards.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Your Representations
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            You represent and warrant that: (a) you own or have the necessary rights, licenses, consents, and permissions to submit your User Content and to grant the license above; (b) your User Content does not and will not infringe, misappropriate, or violate any third party's intellectual property, privacy, publicity, or other rights, or any applicable law; and (c) you will not submit content that is unlawful, harassing, abusive, defamatory, obscene, deceptive, or otherwise objectionable.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Display in Social Features
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Profile images, display names, achievement events, streaks, and scores may be shown to other Budgit users in leaderboards, the Discover community feed, and related social features. You control visibility of this content through your privacy settings. You may opt out of leaderboard display by enabling "Leaderboard Opt-Out" and may opt out of the community feed by enabling "Feed Opt-Out" in your Profile settings. See our <Link href="/privacy#social-and-community-features">Privacy Policy</Link> for more information about how we display and share this information.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Removal and Enforcement
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Budgit may, but is not obligated to, review, screen, remove, or restrict any User Content at any time, with or without notice, for any reason, including if we believe it violates these Terms or applicable law. We are not responsible for User Content submitted by you or other users.
          </Typography>
        </Section>

        <Section id="financial-data-and-ai" title="Financial Data and AI Processing" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            "Financial Data" means account, balance, transaction, merchant, category, and related information obtained from your linked financial institutions (including via Plaid), and any receipts, bill images, or similar documents you upload for processing — whether or not you "display" that information to other users.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            You retain your rights in your Financial Data. You grant Budgit a limited license to host, process, analyze, and display Financial Data solely to:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">(a) provide and operate the Service for you (including categorization, budgeting, insights, and support); and</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">(b) improve models and features for your own account (per-user improvement).</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2, mb: 2 }}>
            Pooled improvement (using your Financial Data to improve models and features for other users or the Service generally) is not currently performed and is paused. If we resume pooled improvement, we will update these Terms and the Privacy Policy and, where we offer it, provide an in-product control before relying on that use.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Budgit uses third-party processors, including OpenAI, L.L.C. (or its affiliates), to perform classification, extraction, and related AI tasks on Financial Data and bill/receipt images. Those processors process data on Budgit's instructions as service providers/processors and are further described in the Privacy Policy.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Category corrections and feedback you provide may be used under (b) above.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            This license does not sell your Financial Data. It does not authorize Budgit to publish your transaction-level Financial Data in social features.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            AI-generated categorizations and insights remain informational only and are not financial, tax, or investment advice.
          </Typography>
        </Section>

        <Section id="copyright-complaints-dmca" title="Copyright Complaints (DMCA)" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            If you believe content on the Service infringes your copyright, send a notice that substantially complies with 17 U.S.C. §512(c)(3) to:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, whiteSpace: 'pre-line' }}>
            {`DMCA Designated Agent
Bleau Consulting LLC, doing business as Budgit
11985 S. Monticello Terrace
Olathe, KS 66061
Email: support@budgit.app
(Subject line: "DMCA Notice")`}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            We may remove or disable access to material claimed to be infringing and may terminate accounts of repeat infringers in appropriate circumstances. Designated-agent registration with the U.S. Copyright Office should be completed before or concurrent with this language shipping. Until registration is confirmed, this section is still useful notice but may not alone perfect the §512 safe harbor.
          </Typography>
        </Section>

        <Section id="ai-and-insights" title="AI and Insights Disclaimer" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            AI-generated categorizations and insights are provided "as-is" for informational purposes and may contain inaccuracies. They are not financial advice. Verify information before making decisions. See also the "Financial Data and AI Processing" section above.
          </Typography>
        </Section>

        <Section id="privacy" title="Privacy" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Your use of the Service is subject to our <Link href="/privacy">Privacy Policy</Link>. Sensitive data is encrypted and, where feasible, anonymized at rest as described in our Security and Data Retention documentation.
          </Typography>
        </Section>

        <Section id="disclaimers" title="Disclaimers (No Warranty) and External Services" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            To the maximum extent permitted by law, the Service is provided <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong>, with all faults and without warranties of any kind. Your use of the Service is at your sole risk.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                Budgit disclaims all warranties and conditions, whether express, implied, or statutory, including without limitation any implied warranties of merchantability, fitness for a particular purpose, accuracy, quiet enjoyment, and non-infringement.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                The Service may enable access to third-party services and websites (for example, Plaid, financial institutions, and platform services). These external services are provided by third parties. Budgit does not control and is not responsible for third-party services, and you use them at your own risk.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                You may not use or export or re-export the App except as authorized by United States law and the laws of the jurisdiction in which the App was obtained.
              </Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="limitation-of-liability" title="Limitation of Liability" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            To the maximum extent permitted by law:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Budgit is not liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Our total liability shall not exceed the greater of (i) the total fees you paid to us in the 12 months prior to the event giving rise to liability, or (ii) $100.</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="termination" title="Termination" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            These Terms are effective until terminated. You may stop using the Service at any time. Budgit may suspend or terminate your access to the Service at any time if you violate these Terms or if we reasonably believe your use poses a risk to the Service, other users, or third parties.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Upon termination, the license granted to you will end and you must stop using the App. Account data handling after termination is described in our <Link href="/privacy">Privacy Policy</Link> and related data retention documentation.
          </Typography>
        </Section>

        <Section id="governing-law" title="Governing Law and Dispute Resolution" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Governing Law.</strong> These Terms are governed by the laws of the State of Kansas, without regard to conflict of law principles, except that the Federal Arbitration Act governs the interpretation and enforcement of the arbitration agreement below.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Informal Resolution.</strong> Before filing a claim, you agree to try to resolve the dispute informally by emailing support@budgit.app with a brief description of the dispute and the relief sought. If we cannot resolve it within 30 days, either party may proceed as below.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Binding Arbitration.</strong> Except for the Excluded Claims below, any dispute, claim, or controversy arising out of or relating to these Terms or the Service will be resolved by binding individual arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules. The arbitration will be conducted by a single arbitrator. The seat of arbitration is Johnson County, Kansas. You may choose to appear by video, phone, or in writing where the AAA rules allow.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Class Action Waiver.</strong> YOU AND BUDGIT AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one person's claims or preside over any form of representative or class proceeding.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Excluded Claims.</strong> Either party may bring an individual action in small-claims court if the claim qualifies. Either party may seek injunctive or other equitable relief in a court of competent jurisdiction to protect intellectual property rights or to stop unauthorized access or abuse of the Service. If the class-action waiver is found unenforceable as to a particular claim, that claim (and only that claim) must proceed in court, not arbitration. Subject to the foregoing and any mandatory consumer protections that apply in your place of residence, court proceedings permitted by this section must be brought in the state or federal courts located in Johnson County, Kansas, and you consent to the personal jurisdiction and venue of those courts.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Opt-Out.</strong> You may opt out of this arbitration agreement within 30 days of first accepting these Terms (or of first accepting a version that added this section) by emailing support@budgit.app with subject "Arbitration Opt-Out" and your account email. Opting out does not affect other Terms.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <strong>Jury Trial Waiver.</strong> TO THE EXTENT A CLAIM PROCEEDS IN COURT RATHER THAN ARBITRATION, YOU AND BUDGIT WAIVE ANY RIGHT TO A JURY TRIAL TO THE MAXIMUM EXTENT PERMITTED BY LAW.
          </Typography>
        </Section>

        <Section id="changes-to-terms" title="Changes to Terms" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            We may update these Terms from time to time. Updates will be posted here with an updated "Last updated" date. Your continued use constitutes acceptance of the updated Terms.
          </Typography>
        </Section>

        <Section id="contact" title="Contact" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Questions about these Terms? Contact Bleau Consulting LLC, doing business as Budgit, at <Link href="mailto:support@budgit.app">support@budgit.app</Link>, or write to:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {`Bleau Consulting LLC, doing business as Budgit
11985 S. Monticello Terrace
Olathe, KS 66061`}
          </Typography>
        </Section>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <SideMenu />
          </Grid>
        </Grid>

      </Container>
    </Box>
  );
};

export default Terms;


